import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {FemaRegion} from 'src/app/shared/classes/fema-region';
import {IRegionOption} from 'src/app/shared/interfaces/region-option.interface';
import {IStateOption} from 'src/app/shared/interfaces/state-option.interface';
import {County} from 'src/app/shared/classes/county';
import {State} from 'src/app/shared/classes/state';
import {DataService} from 'src/app/shared/services/data.service';
import {
    IndexDbService,
    RxEIDatabaseResponse,
    EIindexDBCollection,
    RxEIDocumentType,
} from 'src/app/shared/services/indexed-db.service';
import {firstValueFrom, forkJoin, map, Observable} from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class LocationNameGuard implements Resolve<any> {
    private db = inject(IndexDbService);
    private dataService = inject(DataService);

    public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const dbKeys = [
            'stateOptions',
            'regionOptions',
            'statesByFema',
            'sortedStates',
            'sortedRegions',
            'countyGeometries',
        ];

        // First query IndexedDB to see if we have our saved data
        return this.db.simpleQueryByIds(dbKeys).then(async (data) => {
            // If the data we get back does not match the required keys then we fetch it and set it
            if (Object.keys(data).length !== dbKeys.length) {
                // Get the missing data and reassign it and
                // use the mutable saved data to pass to `setData`
                data = await firstValueFrom(this.fetchMissingData())
                    // Then save that data to the DB
                    .then((missingData) => this.saveDataToDb(missingData));
            }

            // If we have the correct data already saved then load it into the app state
            this.setData(data);

            return true;
        });
    }

    /**
     * Fetch, sort, and organize state, county, and fema data the way we like it
     */
    private fetchMissingData(): Observable<RxEIDatabaseResponse> {
        const femaRegions = this.dataService.getFemaNames();
        const stateCall = this.dataService.getStateGeometries();
        const countyCall = this.dataService.getCountyGeometries();

        return forkJoin([femaRegions, stateCall, countyCall]).pipe(
            map(([regionNames, stateGeometries, countyGeometries]) => {
                const sortedRegions = regionNames.sort((a: FemaRegion, b: FemaRegion) => {
                    if (a.id > b.id) {
                        return 1;
                    } else if (a.id < b.id) {
                        return -1;
                    }
                    return 0;
                });

                const sortedStates = stateGeometries.sort((a: State, b: State) => {
                    if (a.abbreviation > b.abbreviation) {
                        return 1;
                    } else if (a.abbreviation < b.abbreviation) {
                        return -1;
                    }
                    return 0;
                });

                const statesByFema = new Map<number, State[]>();
                sortedStates.forEach((s) => {
                    const val = statesByFema.get(s.dotregion) || [];
                    val.push(s);
                    statesByFema.set(s.dotregion, val);
                });

                const stateOptions = sortedStates.map((s) => {
                    return {
                        state: s,
                        selected: false,
                        counties: countyGeometries.filter((c) => c.geoid.slice(0, 2) === s.geoid),
                    };
                });

                const regionOptions = sortedRegions.map((region) => {
                    const regionStates = stateOptions
                        .filter((s) => s.state.dotregion === region.id)
                        .map((rs) => {
                            return {
                                state: rs.state,
                                selected: false,
                                counties: rs.counties,
                            };
                        });
                    return {
                        regionId: region.id,
                        region,
                        states: regionStates,
                        selected: false,
                    };
                });

                return {sortedStates, sortedRegions, regionOptions, stateOptions, statesByFema, countyGeometries};
            }),
        );
    }

    // Save the actual data to the DB
    private async saveDataToDb(newData: RxEIDatabaseResponse): Promise<RxEIDatabaseResponse> {
        // Dynamically build the objects to save
        const upsertData: EIindexDBCollection[] = Object.keys(newData).map((key) => {
            return {
                geoType: key,
                geometries: newData[key],
            };
        });

        const db = await this.db.eiCollection();
        const upsert = await db.bulkUpsert(upsertData as RxEIDocumentType[]);

        // If theres some sort of error try to flag it
        if (upsert.error.length) {
            throw new Error(upsert.error[0].documentId);
        }

        // If we use the data we just saved as the data we set in the state
        // we first have to convert that data to a mutable object
        const results: RxEIDatabaseResponse = {};
        upsert.success.forEach((document) => {
            const doc = document.toMutableJSON(true) as EIindexDBCollection;
            results[doc.geoType] = doc.geometries;
        });

        return results;
    }

    // Set the data in the application state
    private setData(data: RxEIDatabaseResponse) {
        // Options
        this.dataService.stateOptions = data.stateOptions as IStateOption[];
        this.dataService.regionOptions = data.regionOptions as IRegionOption[];

        // States
        this.dataService.stateNames.next(data.sortedStates as State[]);
        this.dataService.formatStateGeometries(data.sortedStates as State[]);
        DataService.states.next(data.sortedStates as State[]);

        // FEMA Regions
        this.dataService.femaNames.next(data.sortedRegions as FemaRegion[]);
        this.dataService.allFemaStatesCounties.set(this.dataService.regionOptions);
        DataService.statesByFema.next(data.statesByFema as Map<number, State[]>);
        DataService.femaRegions.next(data.sortedRegions as FemaRegion[]);

        // Counties
        this.dataService.formatCountyGeometries(data.countyGeometries as County[]);
        DataService.counties.next(data.countyGeometries as County[]);
    }
}
